/**
 * Redis-cached stat-card counts for OpportunitiesDashboard (total / open / tracked / deadlines).
 * Single stable React Query entry per project + geographic zone.
 */

import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import {
  getGeographicZoneFilterValue,
  type GeographicZoneId,
} from '../utils/countryAccess';

export interface OpportunitiesDashboardStats {
  total: number;
  open_count: number;
  tracked_count: number;
  upcoming_deadlines_count: number;
  cached_at: string | null;
}

export const OPPORTUNITIES_DASHBOARD_STATS_QUERY_KEY = 'govt-opportunities-dashboard-stats';

/** Stable cache key segment — always use `world` instead of omitting the zone. */
export function opportunitiesDashboardStatsZoneKey(zoneId: GeographicZoneId): string {
  return getGeographicZoneFilterValue(zoneId) ?? 'world';
}

export function opportunitiesDashboardStatsQueryKey(
  projectId: string,
  zoneId: GeographicZoneId
): readonly [typeof OPPORTUNITIES_DASHBOARD_STATS_QUERY_KEY, string, string] {
  return [OPPORTUNITIES_DASHBOARD_STATS_QUERY_KEY, projectId, opportunitiesDashboardStatsZoneKey(zoneId)];
}

export function useOpportunitiesDashboardStats(
  projectId: string | null | undefined,
  zoneId: GeographicZoneId,
  options?: { enabled?: boolean }
) {
  const zoneFilter = getGeographicZoneFilterValue(zoneId);
  const enabled = !!projectId && (options?.enabled ?? true);

  return useQuery({
    queryKey: opportunitiesDashboardStatsQueryKey(projectId!, zoneId),
    queryFn: async (): Promise<OpportunitiesDashboardStats | null> => {
      if (!projectId) return null;
      const api = getAPIClient();
      const zoneQuery = zoneFilter ? `&geographic_zone=${encodeURIComponent(zoneFilter)}` : '';
      return api.get<OpportunitiesDashboardStats>(
        `/api/opportunities/dashboard-stats?project_id=${encodeURIComponent(projectId)}${zoneQuery}`
      );
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}
